import { PROVIDER_KEY_IN_CHAT_TEXT } from '@zerohuman/shared'

/**
 * #1716: spots an AI provider key typed into the chat composer, so the portal stops it before it is sent and points
 * the person at the AI account card instead. The API refuses the same message at its send boundary
 * (`apps/api/src/chat/key-shaped.ts`, checked in `ChatService.postMessage`), and that refusal is the guarantee that a
 * key never reaches a model; this is the browser-side feedback on top of it (CTO decision 7 on #1716).
 *
 * The same deliberately loose shape as the server's: an `sk-` token, with or without a provider tag (`sk-ant-`,
 * `sk-or-`, `sk-proj-`), whose body is at least 16 characters of key alphabet, so `sk-learn`, `ski-trip` and
 * `task-force` are left alone. Written without a lookbehind, which older browsers cannot parse.
 */
const PROVIDER_KEY = /(?:^|[^A-Za-z0-9_-])sk-(?:ant-|or-|proj-)?[A-Za-z0-9_-]{16,}/

export function looksLikeProviderKey(text: string): boolean {
    return PROVIDER_KEY.test(text)
}

/** The API's refusal code for a chat message that carries a key (`PROVIDER_KEY_IN_MESSAGE`). */
export const PROVIDER_KEY_IN_MESSAGE = 'provider_key_in_message'

/**
 * What the person reads when a key is stopped: the one shared sentence (#2150), which the API's refusal
 * (`PROVIDER_KEY_IN_MESSAGE_TEXT`) is too, so it says the same whether the portal or the server caught the key.
 */
export { PROVIDER_KEY_IN_CHAT_TEXT } from '@zerohuman/shared'

/**
 * Whether a failed send is the API refusing a key. `request()` throws the refusal's sentence when it carries one and
 * its code otherwise, so either counts.
 */
export function isProviderKeyRefusal(caught: unknown): boolean {
    return (
        caught instanceof Error &&
        (caught.message === PROVIDER_KEY_IN_MESSAGE || caught.message === PROVIDER_KEY_IN_CHAT_TEXT)
    )
}
